// El crédito del cliente abierto en la ficha 360: sus créditos, el plan de pagos del vigente y la
// metadata de sus contratos. El acreditado sale del mismo lote que alimenta la tabla.
import { useQuery } from '@tanstack/react-query';
import type { LoanContractResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanContractResponse';
import type { LoanCreditResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanCreditResponse';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import {
    listBorrowerCredits,
    listBorrowersPortfolio,
    listCreditContracts,
    listCreditInstallments,
    type LoanBorrowerCreditItem,
    type LoanPortfolioResponse,
} from '@/api/services/loan-credit';
import { openCreditOf } from '../lib/creditPortfolio';
import { creditReadStateOf } from './creditPortfolioLoader';

export interface CustomerCreditData {
    /** Todos los créditos del acreditado, del más reciente al más viejo. */
    credits: LoanBorrowerCreditItem[];
    /** El crédito vigente que la ficha desglosa; `null` si no hay ninguno. */
    activeCredit: LoanCreditResponse | null;
    /** Cuotas del crédito vigente. Vacío cuando no hay vigente. */
    installments: LoanInstallmentResponse[];
    contracts: LoanContractResponse[];
}

export type CustomerCreditDetail =
    | { state: 'ready'; data: CustomerCreditData }
    | { state: 'loading' }
    | { state: 'no-source' }
    | { state: 'error' }
    | { state: 'forbidden' };

export interface CustomerCreditDeps {
    listBorrowersPortfolio: (retailCustomerIds: string[]) => Promise<LoanPortfolioResponse>;
    listBorrowerCredits: (borrowerId: string) => Promise<LoanBorrowerCreditItem[]>;
    listCreditInstallments: (creditId: string) => Promise<LoanInstallmentResponse[]>;
    listCreditContracts: (creditId: string) => Promise<LoanContractResponse[]>;
}

export const DEFAULT_CUSTOMER_CREDIT_DEPS: CustomerCreditDeps = {
    listBorrowersPortfolio,
    listBorrowerCredits,
    listCreditInstallments,
    listCreditContracts,
};

/** La ficha de quien no tiene acreditado: no tiene créditos, y eso es un dato, no una falla. */
const NO_CREDITS: CustomerCreditData = {
    credits: [],
    activeCredit: null,
    installments: [],
    contracts: [],
};

/** El crédito de UN cliente. Nunca lanza: cada desenlace vuelve tipado para que la ficha lo diga. */
export async function loadCustomerCredit(
    retailCustomerId: string,
    deps: CustomerCreditDeps,
): Promise<CustomerCreditDetail> {
    try {
        const { items } = await deps.listBorrowersPortfolio([retailCustomerId]);
        if (items.length === 0) return { state: 'error' };

        const [item] = items;
        if (item.status === 'NO_BORROWER') return { state: 'ready', data: NO_CREDITS };
        if (item.status !== 'OK' || !item.borrowerId) return { state: 'error' };

        const credits = [...(await deps.listBorrowerCredits(item.borrowerId))].sort((a, b) =>
            b.createdAt.localeCompare(a.createdAt),
        );
        const activeCredit = openCreditOf(credits);
        const [installments, contracts] = activeCredit
            ? await Promise.all([
                  deps.listCreditInstallments(activeCredit.creditId),
                  deps.listCreditContracts(activeCredit.creditId),
              ])
            : [[], []];

        return { state: 'ready', data: { credits, activeCredit, installments, contracts } };
    } catch (error) {
        return { state: creditReadStateOf(error) };
    }
}

export interface CustomerCreditResult {
    detail: CustomerCreditDetail;
    /** Lo que dispara el botón «Reintentar» de la ficha cuando la lectura del crédito falló. */
    refetch: () => void;
}

/** El crédito del cliente seleccionado. Deshabilitado mientras no haya selección. */
export function useCustomerCredit(retailCustomerId: string | null): CustomerCreditResult {
    const query = useQuery({
        queryKey: ['loan-credit', 'customer', retailCustomerId],
        queryFn: () => loadCustomerCredit(retailCustomerId ?? '', DEFAULT_CUSTOMER_CREDIT_DEPS),
        enabled: retailCustomerId !== null,
        staleTime: 60_000,
    });

    return {
        detail: detailOf(query.data, query.error),
        refetch: () => {
            void query.refetch();
        },
    };
}

function detailOf(data: CustomerCreditDetail | undefined, error: unknown): CustomerCreditDetail {
    if (data) return data;
    return error ? { state: 'error' } : { state: 'loading' };
}
